import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  FileText, 
  Sparkles, 
  ArrowLeft,
  Download,
  Loader2,
  Calendar,
  Clock,
  Users,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function MeetingMinutesPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [meeting, setMeeting] = useState(null);
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (meetingId) {
      fetchMeeting();
    }
  }, [meetingId]);

  const fetchMeeting = async () => {
    try {
      const response = await api.get(`/meetings/${meetingId}`);
      setMeeting(response.data);
      // Pre-fill notes if they exist
      if (response.data.notes?.length > 0) {
        const existingNotes = response.data.notes.map(n => n.content).join('\n\n');
        setNotes(existingNotes);
      }
    } catch (error) {
      toast.error('Meeting not found');
      navigate('/meetings');
    } finally {
      setLoading(false);
    }
  };

  const generateMinutes = async () => {
    if (!notes.trim()) {
      toast.error('Please enter meeting notes first');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.post('/meetings/generate-minutes', {
        meeting_id: meetingId,
        transcript: notes
      });
      
      setMeeting(prev => ({
        ...prev,
        minutes: response.data.minutes,
        action_items: response.data.minutes.action_items
      }));
      
      toast.success('Meeting minutes generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate minutes');
    } finally {
      setGenerating(false);
    }
  };

  const downloadMinutes = () => {
    if (!meeting?.minutes) return;
    
    const content = `
MEETING MINUTES
================
Title: ${meeting.title}
Date: ${meeting.scheduled_date}
Time: ${meeting.start_time} - ${meeting.end_time}
Type: ${meeting.meeting_type}

SUMMARY
-------
${meeting.minutes.summary || 'N/A'}

KEY DISCUSSIONS
---------------
${meeting.minutes.key_discussions?.map(d => `- ${d.topic}: ${d.details}`).join('\n') || 'N/A'}

DECISIONS
---------
${meeting.minutes.decisions?.map(d => `- ${d}`).join('\n') || 'N/A'}

ACTION ITEMS
------------
${'Sr No | Discussed Topic | Responsible Person | Action Taken | Yet to Take'}
${'------|-----------------|-------------------|--------------|-------------'}
${meeting.minutes.action_items?.map(item => 
  `${item.sr_no} | ${item.discussed_topic} | ${item.responsible_person} | ${item.action_taken || '-'} | ${item.yet_to_take || '-'}`
).join('\n') || 'No action items'}

NEXT MEETING
------------
${meeting.minutes.next_meeting || 'To be scheduled'}
    `;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Meeting_Minutes_${meeting.title.replace(/\s+/g, '_')}_${meeting.scheduled_date}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  if (!meeting) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/meetings')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-amber-500" />
            AI Meeting Minutes
          </h1>
          <p className="text-slate-500 mt-1">Generate intelligent meeting minutes with action items</p>
        </div>
      </div>

      {/* Meeting Info */}
      <Card className="card-icrcl">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-[#1e3a5f]">{meeting.title}</h2>
              <p className="text-sm text-slate-500">{meeting.description}</p>
            </div>
            <div className="flex items-center gap-4 ml-auto text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {meeting.scheduled_date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {meeting.start_time} - {meeting.end_time}
              </span>
              <Badge>{meeting.meeting_type}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notes Input */}
        <Card className="card-icrcl">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Meeting Notes / Transcript
            </CardTitle>
            <CardDescription>
              Enter your meeting notes, discussion points, or paste a transcript. The AI will analyze and generate structured minutes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={`Enter meeting notes here...

Example:
- Discussed Q1 performance metrics
- John presented the new marketing strategy
- Decision: Increase social media budget by 20%
- Sarah to prepare the implementation plan by next Friday
- Need to follow up with the design team on new branding
- Next meeting scheduled for April 5th`}
              className="min-h-[400px]"
              data-testid="meeting-notes-input"
            />
            
            <Button
              onClick={generateMinutes}
              disabled={generating || !notes.trim()}
              className="w-full bg-gradient-to-r from-[#1e3a5f] to-[#2a9d8f] hover:opacity-90"
              data-testid="generate-minutes-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating AI Minutes...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate AI Minutes (Notebook LLM)
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Generated Minutes */}
        <Card className="card-icrcl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Generated Minutes
                </CardTitle>
                <CardDescription>
                  AI-powered meeting summary with action items table
                </CardDescription>
              </div>
              {meeting.minutes && (
                <Button variant="outline" size="sm" onClick={downloadMinutes}>
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {meeting.minutes ? (
              <div className="space-y-6">
                {/* Summary */}
                <div>
                  <Label className="text-sm font-medium text-slate-500">Summary</Label>
                  <p className="mt-1 text-slate-700">{meeting.minutes.summary}</p>
                </div>

                {/* Key Discussions */}
                {meeting.minutes.key_discussions?.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium text-slate-500">Key Discussions</Label>
                    <ul className="mt-1 space-y-2">
                      {meeting.minutes.key_discussions.map((item, i) => (
                        <li key={i} className="bg-slate-50 p-3 rounded-lg">
                          <p className="font-medium text-[#1e3a5f]">{item.topic}</p>
                          <p className="text-sm text-slate-600">{item.details}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Decisions */}
                {meeting.minutes.decisions?.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium text-slate-500">Decisions Made</Label>
                    <ul className="mt-1 space-y-1">
                      {meeting.minutes.decisions.map((decision, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-500" />
                          <span>{decision}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Action Items Table */}
                {meeting.minutes.action_items?.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium text-slate-500 mb-2 block">
                      Action Items Table
                    </Label>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-[#1e3a5f] text-white">
                          <tr>
                            <th className="p-3 text-left">Sr No</th>
                            <th className="p-3 text-left">Discussed Topics</th>
                            <th className="p-3 text-left">Responsible Person</th>
                            <th className="p-3 text-left">Action Taken</th>
                            <th className="p-3 text-left">Yet to Take</th>
                          </tr>
                        </thead>
                        <tbody>
                          {meeting.minutes.action_items.map((item, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                              <td className="p-3 font-medium">{item.sr_no}</td>
                              <td className="p-3">{item.discussed_topic}</td>
                              <td className="p-3">
                                <Badge variant="outline">{item.responsible_person}</Badge>
                              </td>
                              <td className="p-3">
                                {item.action_taken ? (
                                  <span className="flex items-center gap-1 text-green-600">
                                    <CheckCircle2 className="w-4 h-4" />
                                    {item.action_taken}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              <td className="p-3">
                                {item.yet_to_take ? (
                                  <span className="flex items-center gap-1 text-amber-600">
                                    <AlertCircle className="w-4 h-4" />
                                    {item.yet_to_take}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Next Meeting */}
                {meeting.minutes.next_meeting && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <Label className="text-sm font-medium text-blue-700">Next Meeting</Label>
                    <p className="mt-1 text-blue-800">{meeting.minutes.next_meeting}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Sparkles className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No minutes generated yet</p>
                <p className="text-sm text-slate-400 mt-1">
                  Enter your meeting notes and click "Generate AI Minutes"
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
